import React, { useState } from "react";
import {
  Check,
  X,
  Users,
  Sparkles,
  Zap,
  Crown,
  ChevronRight,
} from "lucide-react";

export default function FacultyPricing() {
  const [facultyCount, setFacultyCount] = useState(10);
  const [billingCycle, setBillingCycle] = useState("monthly");

  const plans = [
    {
      name: "Starter",
      icon: Sparkles,
      description: "Perfect for small institutions getting started",
      pricePerFaculty: billingCycle === "monthly" ? 12 : 10,
      minFaculty: 1,
      maxFaculty: 25,
      color: "from-emerald-500 to-teal-600",
      features: [
        { text: "Basic course management", included: true },
        { text: "Student performance tracking", included: true },
        { text: "Email support", included: true },
        { text: "Mobile app access", included: true },
        { text: "Advanced analytics", included: false },
        { text: "API access", included: false },
        { text: "Priority support", included: false },
      ],
      popular: false,
    },
    {
      name: "Professional",
      icon: Zap,
      description: "Ideal for growing institutions",
      pricePerFaculty: billingCycle === "monthly" ? 18 : 15,
      minFaculty: 10,
      maxFaculty: 100,
      color: "from-violet-500 to-purple-600",
      features: [
        { text: "Everything in Starter", included: true },
        { text: "Advanced analytics dashboard", included: true },
        { text: "Custom branding", included: true },
        { text: "Integration with LMS", included: true },
        { text: "Priority email support", included: true },
        { text: "API access", included: true },
        { text: "Dedicated account manager", included: false },
      ],
      popular: true,
    },
    {
      name: "Enterprise",
      icon: Crown,
      description: "For large institutions with advanced needs",
      pricePerFaculty: billingCycle === "monthly" ? 25 : 20,
      minFaculty: 50,
      maxFaculty: null,
      color: "from-amber-500 to-orange-600",
      features: [
        { text: "Everything in Professional", included: true },
        { text: "Unlimited integrations", included: true },
        { text: "Advanced security features", included: true },
        { text: "Custom development", included: true },
        { text: "Dedicated account manager", included: true },
        { text: "24/7 phone support", included: true },
        { text: "On-premise deployment option", included: true },
      ],
      popular: false,
    },
  ];

  const calculatePrice = (plan) => {
    const count = Math.max(
      plan.minFaculty,
      Math.min(facultyCount, plan.maxFaculty || facultyCount)
    );
    const basePrice = count * plan.pricePerFaculty;
    return basePrice;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-violet-600/10 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>
        <div
          className="absolute top-1/2 left-1/2 w-96 h-96 bg-amber-600/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {/* Header */}
        <div className="text-center mb-16 animate-fadeIn">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/10 border border-violet-500/20 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-violet-400" />
            <span className="text-sm text-violet-300 font-medium tracking-wide">
              FLEXIBLE PRICING
            </span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-violet-100 to-emerald-100 bg-clip-text text-transparent leading-tight">
            Pay Per Faculty Member
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto font-light">
            Scale effortlessly with transparent pricing. Only pay for the
            faculty members you need.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-full p-1.5">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-8 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                billingCycle === "monthly"
                  ? "bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/50"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`px-8 py-3 rounded-full text-sm font-semibold transition-all duration-300 relative ${
                billingCycle === "annual"
                  ? "bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/50"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Annual
              <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Faculty Calculator */}
        <div className="max-w-2xl mx-auto mb-16 bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-violet-400" />
              <h3 className="text-xl font-bold">Number of Faculty Members</h3>
            </div>
            <div className="text-3xl font-bold bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
              {facultyCount}
            </div>
          </div>
          <input
            type="range"
            min="1"
            max="200"
            value={facultyCount}
            onChange={(e) => setFacultyCount(parseInt(e.target.value))}
            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-violet-500"
            style={{
              background: `linear-gradient(to right, rgb(139 92 246) 0%, rgb(139 92 246) ${
                (facultyCount / 200) * 100
              }%, rgb(51 65 85) ${
                (facultyCount / 200) * 100
              }%, rgb(51 65 85) 100%)`,
            }}
          />
          <div className="flex justify-between text-sm text-slate-500 mt-2">
            <span>1 faculty</span>
            <span>200+ faculty</span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            const price = calculatePrice(plan);
            const isRecommended =
              facultyCount >= plan.minFaculty &&
              (plan.maxFaculty === null || facultyCount <= plan.maxFaculty);

            return (
              <div
                key={plan.name}
                className={`relative bg-slate-800/40 backdrop-blur-xl border rounded-2xl p-8 transition-all duration-500 hover:scale-105 hover:shadow-2xl ${
                  plan.popular
                    ? "border-violet-500/50 shadow-xl shadow-violet-500/20 md:-mt-4 md:scale-105"
                    : "border-slate-700/50"
                } ${isRecommended ? "ring-2 ring-emerald-500/50" : ""}`}
                style={{
                  animationDelay: `${index * 100}ms`,
                  animation: "slideUp 0.6s ease-out forwards",
                }}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="bg-gradient-to-r from-violet-500 to-purple-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                      MOST POPULAR
                    </div>
                  </div>
                )}

                {isRecommended && (
                  <div className="absolute -top-4 right-4">
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      RECOMMENDED
                    </div>
                  </div>
                )}

                <div
                  className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${plan.color} mb-6`}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>

                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                  {plan.description}
                </p>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-5xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                      ${price.toLocaleString()}
                    </span>
                    <span className="text-slate-500 text-sm">
                      /{billingCycle === "monthly" ? "mo" : "yr"}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500">
                    ${plan.pricePerFaculty} per faculty •{" "}
                    {billingCycle === "monthly" ? "monthly" : "annual"} billing
                  </div>
                  {plan.minFaculty && (
                    <div className="text-xs text-slate-600 mt-1">
                      {plan.minFaculty}-{plan.maxFaculty || "∞"} faculty members
                    </div>
                  )}
                </div>

                <button
                  className={`w-full py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 group ${
                    plan.popular
                      ? "bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white shadow-lg shadow-violet-500/50"
                      : "bg-slate-700/50 hover:bg-slate-700 text-white border border-slate-600/50"
                  }`}
                >
                  Get Started
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>

                <div className="mt-8 pt-8 border-t border-slate-700/50">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm">
                        {feature.included ? (
                          <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <X className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
                        )}
                        <span
                          className={
                            feature.included
                              ? "text-slate-300"
                              : "text-slate-600"
                          }
                        >
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mt-24">
          <h2 className="text-3xl font-bold text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {[
              {
                q: "Can I change my plan later?",
                a: "Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and well prorate the difference.",
              },
              {
                q: "What happens if I add more faculty mid-cycle?",
                a: "You can add faculty members anytime. Well charge a prorated amount for the remainder of your billing cycle.",
              },
              {
                q: "Is there a free trial?",
                a: "Yes! We offer a 14-day free trial with full access to all features. No credit card required.",
              },
              {
                q: "Do you offer discounts for educational institutions?",
                a: "Yes! We offer special discounts for non-profit educational institutions. Contact our sales team for details.",
              },
            ].map((faq, idx) => (
              <div
                key={idx}
                className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 hover:border-violet-500/30 transition-all duration-300"
              >
                <h3 className="text-lg font-semibold mb-2 text-white">
                  {faq.q}
                </h3>
                <p className="text-slate-400 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Signals */}
        <div className="mt-24 text-center">
          <p className="text-slate-500 mb-6">
            Trusted by leading institutions worldwide
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-50">
            <div className="text-2xl font-bold">Stanford</div>
            <div className="text-2xl font-bold">MIT</div>
            <div className="text-2xl font-bold">Oxford</div>
            <div className="text-2xl font-bold">Harvard</div>
            <div className="text-2xl font-bold">Cambridge</div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-24 text-center bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-emerald-500/10 border border-violet-500/20 rounded-2xl p-12">
          <h2 className="text-3xl font-bold mb-4">Ready to get started?</h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Join thousands of institutions already using our platform. Start
            your free trial today.
          </p>
          <button className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-violet-500/50 transition-all duration-300 hover:scale-105">
            Start 14-Day Free Trial
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out;
        }
      `}</style>
    </div>
  );
}
